import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, ArrowRight, ArrowLeft, Sparkle } from "@phosphor-icons/react";

// Ordered tour steps. `target` is the CSS selector of the element to spotlight.
// If the target isn't found on the current viewport, the step still shows a
// centered card so mobile users get the full walkthrough.
const STEPS = [
  {
    id: "welcome",
    title: "Welcome to MyResumeDesign",
    body: "A 30-second tour of the essentials. You can restart it any time from the tour button in the top bar.",
    target: null,
    placement: "center",
  },
  {
    id: "template",
    title: "Pick a template",
    body: "Nine editorial templates — from clean ATS-safe layouts to bold two-column designs. Click any card to preview it live on the right.",
    target: '[data-testid="template-modern"]',
    placement: "right",
  },
  {
    id: "layout",
    title: "Tune layout & density",
    body: "Switch between A4 / US-Letter, comfortable / compact density, and 8–12 pt font size. Or click Auto-fit to squeeze onto one page.",
    target: '[data-testid="layout-controls"]',
    placement: "right",
  },
  {
    id: "sample",
    title: "Load a role-based sample",
    body: "New to résumés? Try a sample for CS Student, Doctor, Sales Manager, Accountant, or Software Engineer. Edit and personalise from there.",
    target: '[data-testid="load-sample-button"]',
    placement: "bottom",
  },
  {
    id: "import",
    title: "Import an existing résumé",
    body: "Have a PDF or DOCX already? Upload it and AI will parse the sections in seconds, ready to refine.",
    target: '[data-testid="upload-resume-button"]',
    placement: "bottom",
  },
  {
    id: "jd",
    title: "Match to a Job Description",
    body: "Paste a JD and AI will rewrite your bullets and summary using the JD's keywords — without inventing facts. It also recommends a template.",
    target: '[data-testid="open-jd-button"]',
    placement: "bottom",
  },
  {
    id: "form",
    title: "Fill in your details",
    body: "Eight collapsible sections — personal, summary, experience, education, skills, projects, certifications, and languages. AI-polish is available on every bullet.",
    target: '[data-testid="resume-form"]',
    placement: "right",
  },
  {
    id: "preview",
    title: "Live preview",
    body: "Everything you type reflects here instantly. Content that spills onto page 2 stays intact (no mid-section breaks).",
    target: '[data-testid="resume-preview"]',
    placement: "left",
  },
  {
    id: "download",
    title: "Download the PDF",
    body: "One-time USD 1 payment unlocks the watermark-free PDF for up to 3 downloads in this session. Payments final — please review before paying.",
    target: '[data-testid="download-pdf-button"]',
    placement: "bottom",
  },
];

const STORAGE_KEY = "myresumedesign_tour_done";

export default function OnboardingTour({ open, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);

  const step = STEPS[stepIdx];
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;

  // Compute the target element's bounding rect + scroll it into view.
  useLayoutEffect(() => {
    if (!open) return;
    if (!step.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const raf = requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, stepIdx, step.target]);

  // Recompute rect on window resize / scroll while tour is open.
  useEffect(() => {
    if (!open) return;
    const onChange = () => {
      if (!step.target) return;
      const el = document.querySelector(step.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [open, step.target]);

  // Keyboard shortcuts: → next, ← back, Esc close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, stepIdx]);

  if (!open) return null;

  const next = () => {
    if (isLast) close();
    else setStepIdx((i) => i + 1);
  };
  const back = () => {
    if (!isFirst) setStepIdx((i) => i - 1);
  };
  const close = () => {
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ }
    setStepIdx(0);
    onClose?.();
  };

  // ---------- position the tour card next to the spotlight ----------
  const PADDING = 8;    // spotlight padding around the target
  const GAP = 14;       // gap between spotlight and card
  const CARD_W = 340;
  const CARD_H_EST = 200;

  let cardStyle = {};
  if (!rect || step.placement === "center") {
    cardStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    };
  } else {
    const t = rect.top - PADDING;
    const l = rect.left - PADDING;
    const w = rect.width + 2 * PADDING;
    const h = rect.height + 2 * PADDING;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let placement = step.placement;

    // Fallback if there isn't enough room
    if (placement === "right" && l + w + GAP + CARD_W > vw - 12) placement = "left";
    if (placement === "left" && l - GAP - CARD_W < 12) placement = "bottom";
    if (placement === "bottom" && t + h + GAP + CARD_H_EST > vh - 12) placement = "top";
    if (placement === "top" && t - GAP - CARD_H_EST < 12) placement = "bottom";

    if (placement === "right") {
      cardStyle = { top: Math.max(16, Math.min(vh - CARD_H_EST - 16, t + h / 2 - CARD_H_EST / 2)), left: l + w + GAP };
    } else if (placement === "left") {
      cardStyle = { top: Math.max(16, Math.min(vh - CARD_H_EST - 16, t + h / 2 - CARD_H_EST / 2)), left: Math.max(12, l - CARD_W - GAP) };
    } else if (placement === "bottom") {
      cardStyle = { top: t + h + GAP, left: Math.max(12, Math.min(vw - CARD_W - 12, l + w / 2 - CARD_W / 2)) };
    } else {
      cardStyle = { top: Math.max(16, t - CARD_H_EST - GAP), left: Math.max(12, Math.min(vw - CARD_W - 12, l + w / 2 - CARD_W / 2)) };
    }
  }

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour" role="dialog" aria-modal="true">
      {/* Backdrop with spotlight cutout via box-shadow */}
      {rect && step.placement !== "center" ? (
        <div
          style={{
            position: "fixed",
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + 2 * PADDING,
            height: rect.height + 2 * PADDING,
            boxShadow: "0 0 0 9999px rgba(10, 10, 10, 0.72)",
            border: "2px solid #2400FF",
            borderRadius: 6,
            pointerEvents: "none",
            transition: "all 240ms ease",
          }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="fixed inset-0 bg-black/70" />
      )}

      {/* Tour card */}
      <div
        ref={cardRef}
        className="fixed bg-white border border-black/10 shadow-2xl"
        style={{ ...cardStyle, width: CARD_W, transition: "top 240ms ease, left 240ms ease" }}
        data-testid="tour-card"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
          <div className="flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-[#FF2A00]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              Tour · Step {stepIdx + 1} / {STEPS.length}
            </span>
          </div>
          <button
            onClick={close}
            className="text-neutral-400 hover:text-black"
            aria-label="Close tour"
            data-testid="tour-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="px-4 py-4">
          <div className="font-heading font-semibold text-base tracking-tight">
            {step.title}
          </div>
          <div className="mt-1.5 text-sm text-neutral-700 leading-relaxed">
            {step.body}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-black/10 flex items-center justify-between">
          <button
            onClick={close}
            className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-black"
            data-testid="tour-skip"
          >
            Skip
          </button>
          <div className="flex items-center gap-1.5">
            <button
              onClick={back}
              disabled={isFirst}
              className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2.5 py-1.5 border border-black/10 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tour-back"
            >
              <ArrowLeft size={11} weight="bold" /> Back
            </button>
            <button
              onClick={next}
              className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest px-2.5 py-1.5 bg-neutral-950 text-white hover:bg-neutral-800"
              data-testid="tour-next"
            >
              {isLast ? "Finish" : "Next"} <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Exposed helper so callers can check whether the tour has already been
// completed for this session.
export function hasCompletedTour() {
  try { return sessionStorage.getItem(STORAGE_KEY) === "1"; }
  catch (e) { return false; }
}
